// МЧМ.1: экран выбора режима PvP (мульти-очередь по диздоку §4).
//
// Источник UX-прототипа: profile-prototype.html → #pvp_screen.
//
// Логика:
// - 3 режима очереди: casual (cyan), rated (amber), war (red).
//   Можно выбрать несколько одновременно (мульти-очередь, §4.4 history).
// - Доступ к Войне открывается с лиги Стритраннеры (§4.4 + §5.5).
// - При выборе Войны открывается модалка выбора ставки (1/2/5/10/25/50/100 USDT).
//   Заморозка на счёте = ставка × 4 (максимум при ×4 удвоении).
// - Выбор колоды из списка моих (валидные = HeroId есть в коллекции +
//   24 карты с лимитами редкости).
// - 3 подсказки (info, modes, war).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { HeroAvatar } from '../components/HeroAvatar';
import { useGameStore } from '../store/useGameStore';
import { getProfile, listDecks } from '../api/client';
import { useTr } from '../i18n/useTr';
import { trLeague } from '../i18n/serverStrings';
import { getLeagueDef } from '../i18n/leagues';
import styles from './PvpScreen.module.css';

type ModeId = 'casual' | 'rated' | 'war';

interface DeckLite {
  id: number;
  name: string;
  heroId: string;
  faction: string;
  valid: boolean;
  winrate?: number | null;
  matches?: number;
}

// Этап 74: дешёвый вход $0.5, хвост $100 убран. Синхронно с allowed-вилкой
// в server/src/colyseus/match-room.ts (там parseFloat).
const WAR_STAKES = [0.5, 1, 2, 5, 10, 25, 50];

// Лиги от которых открывается Война (§5.5: «с лиги Стритраннеры»).
const WAR_OPEN_LEAGUES = new Set([
  'Стритраннеры', 'Нетраннеры', 'Фиксеры', 'Оперативники', 'Корпораты', 'Высшая Лига'
]);

const FACTION_COLOR: Record<string, string> = {
  deus: 'var(--deus, #3D7DFF)',
  syntetics: 'var(--syn, #C93DFF)',
  global: 'var(--gsec, #FF8A1F)',
  livlife: 'var(--liv, #FF3D3D)',
  neutral: 'var(--t-mute, #3A3E4E)'
};
const FACTION_LABEL: Record<string, string> = {
  deus: 'деус', syntetics: 'синтетикс', global: 'глобал', livlife: 'лив лайф', neutral: '—'
};
const FACTION_LABEL_EN: Record<string, string> = {
  deus: 'deus', syntetics: 'synthetics', global: 'global', livlife: 'liv life', neutral: '—'
};

interface HintProps {
  label: string;
  title: string;
  body: string;
  onClose: () => void;
}
function Hint({ label, title, body, onClose }: HintProps) {
  const tr = useTr();
  return (
    <div className={styles.hintOverlay} onClick={onClose}>
      <div className={styles.hintCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.hintLabel}>◆ {label}</div>
        <div className={styles.hintTitle}>{title}</div>
        <div className={styles.hintBody} dangerouslySetInnerHTML={{ __html: body }} />
        <button className={styles.hintOk} onClick={onClose}>{tr('ПОНЯЛ', 'GOT IT')}</button>
      </div>
    </div>
  );
}

export function PvpScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factionLabel = isEn ? FACTION_LABEL_EN : FACTION_LABEL;
  const progress = useGameStore(s => s.progress);

  // ВОЙ.2: показываем ошибку если matchmaking вернул нас из-за нехватки
  // USDT на freeze. state.insufficientUsdt = { need, have } если pre-flight
  // отказал; state.blamedSelf=true если виноват юзер, false — противник.
  const [warError, setWarError] = useState<{
    self: boolean; need?: number; have?: number;
  } | null>(null);
  // ВОЙ.3: ошибка «нейтральная колода в PvP».
  const [neutralError, setNeutralError] = useState(false);
  // Задача «ошибка вместо фабричной колоды»: матч не стартовал, т.к. сервер
  // не смог загрузить реальную колоду. self=true — виноват ты, false — соперник.
  const [deckError, setDeckError] = useState<{ self: boolean } | null>(null);
  useEffect(() => {
    const st: any = location.state || {};
    if (st.warFreezeFailed) {
      setWarError({
        self: !!st.blamedSelf,
        need: st.insufficientUsdt?.need,
        have: st.insufficientUsdt?.have,
      });
      // Снимаем state из URL — иначе при refresh покажется снова.
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (st.neutralDeckRejected) {
      setNeutralError(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (st.deckLoadFailed) {
      setDeckError({ self: !!st.blamedSelf });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const [league, setLeague] = useState<string>(progress.league || 'Трущобы');
  const [balanceUsdt, setBalanceUsdt] = useState<number>(0);
  const [decks, setDecks] = useState<DeckLite[]>([]);
  const [selectedModes, setSelectedModes] = useState<Set<ModeId>>(new Set(['casual']));
  // МЧМ.3: множественный выбор ставок Войны (несколько очередей параллельно).
  // Старое API имело single warStake — теперь массив. Совместимость с
  // отдельным `state.warStake` оставляем для матчмейкинга через первый
  // элемент (на случай если что-то ещё читает).
  const [warStakes, setWarStakes] = useState<number[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null);
  const [stakeOpen, setStakeOpen] = useState(false);
  const [stakeFromToggle, setStakeFromToggle] = useState(false);
  const [hint, setHint] = useState<HintProps | null>(null);

  // Загружаем профиль (баланс, лига) и колоды.
  useEffect(() => {
    (async () => {
      try {
        const profile = await getProfile();
        setLeague(profile.player.league);
        setBalanceUsdt(profile.player.usdt);
        // Винрейт/матчи берём из /api/decks (getProfile их не отдаёт). Best-effort.
        let wrById: Record<number, { winrate: number | null; matches: number }> = {};
        try {
          const full = await listDecks();
          wrById = Object.fromEntries(full.map(d => [d.id, { winrate: d.winrate ?? null, matches: d.matches ?? 0 }]));
        } catch { /* offline — без винрейта */ }
        // Колоды — превратим в lite.
        const list: DeckLite[] = (profile.decks as any[]).map(d => {
          const cardsCount = Array.isArray(d.cards)
            ? d.cards.reduce((a: number, c: any) => a + (c.count || 0), 0)
            : 0;
          // ВОЙ.3: нейтральная колода НЕ играется в PvP (engine ждёт фракцию
          // из 4). Раньше клиент тихо подменял на 'deus' → игрок видел Деус
          // в матче. Теперь помечаем невалидной для PvP.
          const isValidFaction = d.faction && d.faction !== 'neutral';
          return {
            id: d.id,
            name: d.name,
            heroId: d.heroId,
            faction: d.faction,
            valid: cardsCount >= 24 && isValidFaction,
            winrate: wrById[d.id]?.winrate ?? null,
            matches: wrById[d.id]?.matches ?? 0
          };
        });
        setDecks(list);
        // Авто-выбор: избранная или первая валидная.
        const fav = list.find(d => (profile.decks as any[]).find(dd => dd.id === d.id)?.isFavorite && d.valid);
        const first = list.find(d => d.valid);
        setSelectedDeckId((fav?.id) ?? (first?.id) ?? null);
      } catch { /* offline */ }
    })();
  }, []);

  const warAvailable = WAR_OPEN_LEAGUES.has(league);

  const toggleMode = (id: ModeId) => {
    if (id === 'war' && !warAvailable) return;
    const next = new Set(selectedModes);
    if (id === 'war') {
      if (next.has('war')) {
        next.delete('war');
        setSelectedModes(next);
        setWarStakes([]);
      } else {
        // Сначала открываем модалку выбора ставки. Сама очередь добавится
        // после pickStake.
        setStakeFromToggle(true);
        setStakeOpen(true);
      }
    } else {
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedModes(next);
    }
  };

  // МЧМ.3: toggle одной ставки в массиве. Если пусто после toggle —
  // снимаем выбор Войны. Иначе оставляем (или добавляем).
  const toggleStake = (s: number) => {
    setWarStakes((cur) => {
      const has = cur.includes(s);
      const next = has ? cur.filter(x => x !== s) : [...cur, s].sort((a, b) => a - b);
      // Войну в режимах синхронизируем: если ставок 0 — снимаем war, иначе включаем.
      setSelectedModes((mods) => {
        const m = new Set(mods);
        if (next.length === 0) m.delete('war');
        else m.add('war');
        return m;
      });
      return next;
    });
  };

  const closeStake = () => {
    setStakeOpen(false);
    if (stakeFromToggle && warStakes.length === 0) {
      // Юзер отменил — Война не выбирается.
      const next = new Set(selectedModes);
      next.delete('war');
      setSelectedModes(next);
    }
    setStakeFromToggle(false);
  };

  // МЧМ.3: «Готово» — закрыть модалку выбора ставок если выбрано хотя бы 1.
  const confirmStakes = () => {
    if (warStakes.length === 0) {
      closeStake();
      return;
    }
    setStakeOpen(false);
    setStakeFromToggle(false);
    setSelectedModes((mods) => {
      const m = new Set(mods);
      m.add('war');
      return m;
    });
  };

  const selectedDeck = decks.find(d => d.id === selectedDeckId);
  const canSearch = selectedModes.size > 0 && selectedDeck && selectedDeck.valid;

  const btnText = useMemo(() => {
    if (selectedModes.size === 0) return tr('► ВЫБЕРИ РЕЖИМ', '► PICK A MODE');
    if (!selectedDeck) return tr('► ВЫБЕРИ КОЛОДУ', '► PICK A DECK');
    if (selectedModes.size === 1) return tr('► НАЙТИ ПРОТИВНИКА', '► FIND OPPONENT');
    return tr(
      `► НАЙТИ ПРОТИВНИКА · ${selectedModes.size} ОЧЕРЕДИ`,
      `► FIND OPPONENT · ${selectedModes.size} QUEUES`
    );
  }, [selectedModes, selectedDeck, tr]);

  const onSearch = () => {
    if (!canSearch) return;
    // МЧМ.3: проверка — если выбран war, должна быть хотя бы 1 ставка.
    if (selectedModes.has('war') && warStakes.length === 0) return;
    // Передаём настройки матчмейкинга в location state.
    navigate('/pvp/match', {
      state: {
        modes: Array.from(selectedModes),
        // МЧМ.3: warStakes — массив выбранных ставок. warStake (legacy)
        // оставляем для совместимости с UI блоком депозита (max ставки).
        warStakes,
        warStake: warStakes[warStakes.length - 1] ?? null,
        deckId: selectedDeckId,
        deckName: selectedDeck?.name || '—',
        // ПвП.4b: heroId/faction нужны MatchmakingScreen'у чтобы
        // joinOrCreateMatch отправил их серверу как опции комнаты.
        heroId: selectedDeck?.heroId,
        faction: selectedDeck?.faction,
      }
    });
  };

  // ЛИГ.1.x: прогресс до следующей лиги. Для Высшей winsToPromote=null
  // → показываем «ТОП» вместо прогресса.
  const leagueDef = getLeagueDef(league);
  const winsNeeded = leagueDef.winsToPromote;
  const winStreak = progress.leagueWinStreak;
  const lossStreak = (progress as any).leagueLossStreak ?? 0; // если добавим позже

  return (
    <ScreenLayout title={tr('ВЫБОР РЕЖИМА', 'MODE SELECTION')} backTo="/home">
      <div className={styles.screen}>
        {/* ВОЙ.3: ошибка «нейтральная колода в PvP». */}
        {neutralError && (
          <div
            style={{
              padding: '10px 12px',
              marginBottom: 8,
              background: 'rgba(40, 30, 8, 0.9)',
              border: '1px solid var(--neon-am, #FFB13D)',
              color: '#F4F6FB',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              lineHeight: 1.5,
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
            }}
            onClick={() => setNeutralError(false)}
          >
            ⚠ {tr(
              'Нейтральная колода не играется в PvP. Выбери колоду одной из 4 фракций (Деус / Синтетикс / Глобал / Лив Лайф).',
              'Neutral deck cannot be played in PvP. Pick a deck of one of 4 factions (Deus / Syntetics / Global / Liv Life).'
            )}
            <span style={{ float: 'right', opacity: 0.6 }}>✕</span>
          </div>
        )}

        {/* ВОЙ.2: ошибка нехватки USDT при попытке войти в Войну. */}
        {warError && (
          <div
            style={{
              padding: '10px 12px',
              marginBottom: 8,
              background: 'rgba(40, 8, 8, 0.9)',
              border: '1px solid var(--neon-rd, #FF3D3D)',
              color: '#F4F6FB',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              lineHeight: 1.5,
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
            }}
            onClick={() => setWarError(null)}
          >
            ⚠ {warError.self
              ? tr(
                  `Недостаточно USDT для Войны (нужно ${warError.need?.toFixed(2) ?? '?'}, есть ${warError.have?.toFixed(2) ?? '?'}). Пополни баланс в Профиле.`,
                  `Insufficient USDT for War (need $${warError.need?.toFixed(2) ?? '?'}, have $${warError.have?.toFixed(2) ?? '?'}). Top up in Profile.`
                )
              : tr(
                  'У противника не хватило USDT — матч не стартанул. Попробуй ещё раз.',
                  "Opponent's balance was insufficient — match didn't start. Try again."
                )}
            <span style={{ float: 'right', opacity: 0.6 }}>✕</span>
          </div>
        )}

        {/* Ошибка вместо фабричной колоды: матч не стартовал из-за колоды. */}
        {deckError && (
          <div
            style={{
              padding: '10px 12px',
              marginBottom: 8,
              background: 'rgba(40, 8, 8, 0.9)',
              border: '1px solid var(--neon-rd, #FF3D3D)',
              color: '#F4F6FB',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              lineHeight: 1.5,
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
            }}
            onClick={() => setDeckError(null)}
          >
            ⚠ {deckError.self
              ? tr(
                  'Не удалось загрузить твою колоду — матч не стартовал. Проверь колоду в редакторе (должно быть ≥24 карт) и попробуй ещё раз.',
                  "Couldn't load your deck — match didn't start. Check your deck in the editor (needs ≥24 cards) and try again."
                )
              : tr(
                  'У соперника не загрузилась колода — матч не стартовал. Попробуй ещё раз.',
                  "Opponent's deck failed to load — match didn't start. Try again."
                )}
            <span style={{ float: 'right', opacity: 0.6 }}>✕</span>
          </div>
        )}

        {/* Info bar */}
        <div className={styles.infoBar} onClick={() => setHint({
          label: tr('ПОДСКАЗКА', 'HINT'), title: tr('ТВОЯ СВОДКА', 'YOUR SUMMARY'),
          body: tr(
            'Текущая лига, прогресс до следующей лиги, баланс USDT и выбранная ставка для Войны Корпораций. Лига определяет минимум доступных режимов: Война открывается с лиги Стритраннеров.',
            'Current league, progress to next league, USDT balance and chosen Corp Wars stake. League determines available modes: War opens at Streetrunners league.'
          ),
          onClose: () => setHint(null)
        })}>
          <div className={styles.seg}>
            <span className={styles.lbl}>{tr('Лига', 'League')}</span>
            <span className={styles.val}>{trLeague(league)}</span>
          </div>
          <div className={styles.div} />
          <div className={styles.seg}>
            <span className={styles.lbl}>{tr('До подъёма', 'To promote')}</span>
            <span className={styles.val} style={{ color: 'var(--neon-li)' }}>
              {winsNeeded === null
                ? tr('ТОП', 'TOP')
                : `${winStreak} / ${winsNeeded}`}
            </span>
          </div>
          <div className={styles.div} />
          <div className={styles.seg}>
            <span className={styles.lbl}>{tr('Баланс', 'Balance')}</span>
            <span className={`${styles.val} ${styles.usdt}`}>{balanceUsdt.toFixed(2)} USDT</span>
          </div>
          <div className={styles.div} />
          <div className={styles.seg}>
            <span className={styles.lbl}>{tr('Ставки ВК', 'War stakes')}</span>
            <span className={styles.val}>
              {warStakes.length === 0
                ? '—'
                : warStakes.map(s => `$${s}`).join(', ')}
            </span>
          </div>
        </div>

        {/* Mode list */}
        <div className={styles.sectionTitle} onClick={() => setHint({
          label: tr('ПОДСКАЗКА', 'HINT'), title: tr('РЕЖИМЫ И МУЛЬТИ-ОЧЕРЕДЬ', 'MODES & MULTI-QUEUE'),
          body: tr(
            'Можно выбрать <b>несколько режимов одновременно</b>. Поиск идёт во всех выбранных очередях параллельно, и где раньше нашёлся противник — туда матч. Из остальных очередей ты автоматически выпадаешь.<br/><br/>Очереди по сути: <code>casual</code>, <code>rated</code>, <code>war:1</code>, <code>war:2</code>, <code>war:5</code> и т.д. — отдельная очередь на каждую ставку Войны. Соперник в Войне найдётся только среди тех, кто выбрал <b>такую же</b> ставку.',
            'You can pick <b>several modes at once</b>. Search runs in all selected queues in parallel, and whichever finds an opponent first — that\'s your match. You drop out of the rest automatically.<br/><br/>Queues are: <code>casual</code>, <code>rated</code>, <code>war:1</code>, <code>war:2</code>, <code>war:5</code>, etc. — a separate queue per War stake. War opponent is only found among players who picked <b>the same</b> stake.'
          ),
          onClose: () => setHint(null)
        })}>
          <span>► {tr('РЕЖИМЫ ОЧЕРЕДИ', 'QUEUE MODES')}</span>
        </div>
        <div className={styles.modeList}>
          {/* Casual */}
          <button
            className={`${styles.modeCard} ${styles.casual} ${selectedModes.has('casual') ? styles.selected : ''}`}
            onClick={() => toggleMode('casual')}
          >
            <div className={styles.check}>{selectedModes.has('casual') ? '✓' : ''}</div>
            <div className={styles.modeIcon}>⏱</div>
            <div className={styles.modeInfo}>
              <div className={styles.modeTitle}>{tr('ОБЫЧНЫЕ БОИ', 'CASUAL BATTLES')}</div>
              <div className={styles.modeDesc}>{tr(
                'Быстрый поиск без рангов и ставок. Тренируй колоды без риска.',
                'Quick search, no ranks or stakes. Test your decks without risk.'
              )}</div>
              <div className={styles.modeMeta}>+1 <span className={styles.stake}>◆</span> {tr('за победу', 'per win')}</div>
            </div>
          </button>

          {/* Rated */}
          <button
            className={`${styles.modeCard} ${styles.rated} ${selectedModes.has('rated') ? styles.selected : ''}`}
            onClick={() => toggleMode('rated')}
          >
            <div className={styles.check}>{selectedModes.has('rated') ? '✓' : ''}</div>
            <div className={styles.modeIcon}>🛡</div>
            <div className={styles.modeInfo}>
              <div className={styles.modeTitle}>{tr('РЕЙТИНГОВЫЕ БОИ', 'RANKED BATTLES')}</div>
              <div className={styles.modeDesc}>{tr(
                'MMR-подбор, продвижение по лигам, прогресс БП, награды за серии.',
                'MMR matchmaking, league climbs, BP progress, streak rewards.'
              )}</div>
              <div className={styles.modeMeta}>+5–22 <span className={styles.stake}>◆</span> {tr('за победу · +30 поинтов', 'per win · +30 points')}</div>
            </div>
          </button>

          {/* War */}
          <button
            className={`${styles.modeCard} ${styles.war} ${selectedModes.has('war') ? styles.selected : ''} ${!warAvailable ? styles.locked : ''}`}
            onClick={() => toggleMode('war')}
            disabled={!warAvailable}
          >
            <div className={styles.check}>{selectedModes.has('war') ? '✓' : ''}</div>
            <div className={styles.modeIcon}>$</div>
            <div className={styles.modeInfo}>
              <div className={styles.modeTitle}>{tr('ВОЙНА КОРПОРАЦИЙ', 'CORP WARS')}</div>
              {warAvailable ? (
                <>
                  <div className={styles.modeDesc}>{tr(
                    'Опасный режим. Сам выбираешь начальную ставку 1–100 USDT. Снап-механика, дневной лимит 40 матчей.',
                    'Risky mode. Pick your starting stake 1–100 USDT. Snap mechanic, daily limit 40 matches.'
                  )}</div>
                  <div className={styles.modeMeta}>
                    {tr('от', 'from')} <span className={styles.stake}>1</span> {tr('до', 'to')} <span className={styles.stake}>100 USDT</span>
                    {' · '}
                    <span className={styles.danger}>{tr('заморозка до ×4', 'freeze up to ×4')}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.modeDesc}>{isEn ? (
                    <>Unlocks at <b>Streetrunners</b> league. You're at <b>{trLeague(league)}</b> now.</>
                  ) : (
                    <>Открывается с лиги <b>Стритраннеры</b>. Сейчас ты в лиге <b>{trLeague(league)}</b>.</>
                  )}</div>
                  <div className={styles.modeMeta}>🔒 {tr('ЗАБЛОКИРОВАНО', 'LOCKED')}</div>
                </>
              )}
            </div>
          </button>
        </div>

        {/* Warning про Войну */}
        {selectedModes.has('war') && (
          <div className={styles.warn} onClick={() => setHint({
            label: tr('ПОДСКАЗКА', 'HINT'), title: tr('ПРЕДУПРЕЖДЕНИЕ ПО ВОЙНЕ', 'WAR WARNING'),
            body: tr(
              'Война Корпораций — <b>реальные деньги</b>. Депозит замораживается до конца матча. Дневной лимит — 40 матчей. Реванш с тем же игроком — не чаще 1 раза в сутки. Self-exclusion из настроек профиля блокирует Войну на 2 суток.<br/><br/>Каждая ставка — отдельная очередь матчмейкера: чем выше ставка, тем меньше игроков в очереди и дольше поиск.',
              "Corp Wars uses <b>real money</b>. Deposit freezes until match end. Daily limit — 40 matches. Rematch with same player — once per day max. Profile self-exclusion blocks War for 2 days.<br/><br/>Each stake is a separate matchmaker queue: higher stake → fewer players → slower search."
            ),
            onClose: () => setHint(null)
          })}>
            ⚠ {isEn ? (
              <>Corp Wars uses <b>real money</b>. Deposit freezes on your balance until match end. Daily limit — 40 matches. Rematch with same player — once per day max.</>
            ) : (
              <>Война Корпораций — это <b>реальные деньги</b>. Депозит замораживается на счёте до конца матча. Дневной лимит — 40 матчей. Реванш с тем же игроком — не чаще 1 раза в сутки.</>
            )}
          </div>
        )}

        {/* Deck picker */}
        <div className={styles.sectionTitle}>
          <span>► {tr('КОЛОДА', 'DECK')}</span>
          <span className={styles.pickedName}>{selectedDeck ? `${selectedDeck.name}` : '—'}</span>
        </div>
        <div className={styles.deckScroll}>
          {decks.length === 0 && (
            <div style={{ color: 'var(--t-lo)', fontSize: 11, padding: 16 }}>
              {tr('Колод пока нет. Перейди в «Колоды» создать.', 'No decks yet. Go to «Decks» to build one.')}
            </div>
          )}
          {decks.map(d => {
            const isNeutral = d.faction === 'neutral';
            const titleText = isNeutral
              ? tr('Нейтральная колода не играется в PvP — выбери фракцию в редакторе колоды.',
                   'Neutral deck cannot be played in PvP — pick a faction in the deck editor.')
              : !d.valid
                ? tr('Меньше 24 карт', 'Less than 24 cards')
                : '';
            return (
              <button
                key={d.id}
                className={`${styles.deckCard} ${selectedDeckId === d.id ? styles.selected : ''} ${!d.valid ? styles.invalid : ''}`}
                onClick={() => d.valid && setSelectedDeckId(d.id)}
                disabled={!d.valid}
                title={titleText}
              >
                <div className={styles.deckBadge}>★</div>
                <div className={styles.deckHero}>
                  <HeroAvatar id={d.heroId} size={44} faction={d.faction as any} />
                </div>
                <div className={styles.deckName}>{d.name}</div>
                <div className={styles.deckFaction} style={{ color: FACTION_COLOR[d.faction] || 'var(--t-mute)' }}>
                  {isNeutral
                    ? tr('не для PvP', 'not PvP')
                    : d.valid
                      ? factionLabel[d.faction]
                      : tr('не валидна', 'invalid')}
                </div>
                {/* Винрейт колоды (Этап 56). */}
                {d.matches && d.matches > 0 && d.winrate != null ? (
                  <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600, color: d.winrate >= 50 ? 'var(--neon-li)' : '#ff7a7a' }}>
                    {d.winrate}%<span style={{ color: 'var(--t-lo)', fontWeight: 400 }}> · {d.matches}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 10, marginTop: 2, color: 'var(--t-lo)' }}>{tr('нет матчей', 'no matches')}</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Search button */}
        <button
          className={`${styles.searchBtn} ${!canSearch ? styles.disabled : ''} ${selectedModes.has('war') ? styles.hasWar : ''}`}
          onClick={onSearch}
          disabled={!canSearch}
        >
          {btnText}
        </button>
      </div>

      {/* Stake modal — МЧМ.3 multi-select chips */}
      {stakeOpen && (() => {
        const maxStake = warStakes.length > 0 ? Math.max(...warStakes) : 0;
        const totalFreeze = maxStake * 4;
        return (
          <div className={styles.stakeOverlay} onClick={closeStake}>
            <div className={styles.stakeCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.stakeHeader}>
                <div className={styles.stakeTitle}>{tr('ВЫБОР СТАВОК', 'PICK STAKES')}</div>
                <button className={styles.stakeClose} onClick={closeStake}>×</button>
              </div>
              <div className={styles.stakeDesc}>
                {tr(
                  'Можно выбрать несколько ставок — поиск идёт во всех очередях параллельно. Match произойдёт в той, где первой найдётся противник; приоритет — большей ставки.',
                  'You can pick several stakes — search runs in all queues in parallel. Match happens in the first one with an opponent; higher stakes take priority.'
                )}
                <br/><br/>
                {tr(
                  `Заморозится на счёте: $${totalFreeze.toFixed(2)} (max ставка × 4).`,
                  `Frozen on balance: $${totalFreeze.toFixed(2)} (max stake × 4).`
                )}
              </div>
              <div className={styles.stakeGrid}>
                {WAR_STAKES.map(s => {
                  const freeze = s * 4;
                  // Допустимо выбирать ставку только если хватит баланса
                  // на её заморозку. Если уже выбрана — можно снять (логика
                  // дисэйбла не блокирует снятие выбранной ставки).
                  const isPicked = warStakes.includes(s);
                  const disabled = !isPicked && freeze > balanceUsdt;
                  return (
                    <button
                      key={s}
                      className={`${styles.stakeOpt} ${disabled ? styles.disabled : ''} ${isPicked ? styles.selected : ''}`}
                      onClick={() => !disabled && toggleStake(s)}
                      disabled={disabled}
                    >
                      <span className={styles.v}>
                        {isPicked ? '✓ ' : ''}${s.toFixed(s < 10 ? 2 : 0)} USDT
                      </span>
                      <span className={styles.freeze}>{tr(`заморозка $${freeze}`, `freeze $${freeze}`)}</span>
                      {disabled && (
                        <span className={styles.freeze} style={{ color: 'var(--neon-rd, #FF3D3D)' }}>
                          {tr(`нужно ≥ $${freeze}`, `need ≥ $${freeze}`)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                className={styles.searchBtn}
                onClick={confirmStakes}
                disabled={warStakes.length === 0}
                style={{ marginTop: 10 }}
              >
                {warStakes.length === 0
                  ? tr('ВЫБЕРИ ХОТЯ БЫ ОДНУ СТАВКУ', 'PICK AT LEAST ONE STAKE')
                  : tr(`ГОТОВО · ${warStakes.length} ставк${warStakes.length === 1 ? 'а' : warStakes.length < 5 ? 'и' : ''}`, `DONE · ${warStakes.length} stake${warStakes.length === 1 ? '' : 's'}`)}
              </button>
            </div>
          </div>
        );
      })()}

      {hint && <Hint {...hint} />}
    </ScreenLayout>
  );
}
